/**
 * firebasePush — thin wrapper around the Firebase Web SDK for FCM push
 * notifications. All init happens lazily via `/api/push/config` so the
 * project ID + VAPID key stay server-driven (ops can rotate without
 * a frontend redeploy).
 *
 * Everything here is guarded — if the backend reports the provider is
 * not configured, `getPushToken()` returns null and callers can quietly
 * skip. That way the app runs identically whether Firebase is set up
 * or not.
 */
import { api } from "@/lib/api";

interface PushConfig {
  provider: string;
  enabled: boolean;
  web: {
    api_key: string | null;
    auth_domain: string | null;
    project_id: string | null;
    messaging_sender_id: string | null;
    app_id: string | null;
    vapid_public_key: string | null;
  };
}

let cachedConfig: PushConfig | null = null;
let messagingSingleton: any = null;
let inFlight: Promise<any | null> | null = null;

async function loadConfig(): Promise<PushConfig | null> {
  if (cachedConfig) return cachedConfig;
  try {
    const r = await api.get<PushConfig>("/push/config");
    cachedConfig = r.data;
    return cachedConfig;
  } catch {
    return null;
  }
}

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    typeof (window as any).PushManager !== "undefined"
  );
}

async function initMessaging() {
  if (messagingSingleton) return messagingSingleton;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const cfg = await loadConfig();
    if (!cfg?.enabled || !cfg.web.api_key || !cfg.web.project_id || !cfg.web.vapid_public_key) {
      return null;
    }
    // Register the FCM service worker at the app root so it survives
    // route changes.
    let registration: ServiceWorkerRegistration | null = null;
    try {
      registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    } catch {
      return null;
    }

    // Push init config to the service worker so it can decode incoming
    // messages without a rebuild.
    try {
      registration.active?.postMessage({ type: "FIREBASE_CONFIG", config: cfg.web });
    } catch { /* no active worker yet — fine */ }

    const { initializeApp, getApps, getApp } = await import("firebase/app");
    const app = getApps().length ? getApp() : initializeApp({
      apiKey: cfg.web.api_key,
      authDomain: cfg.web.auth_domain || `${cfg.web.project_id}.firebaseapp.com`,
      projectId: cfg.web.project_id,
      messagingSenderId: cfg.web.messaging_sender_id || undefined,
      appId: cfg.web.app_id || undefined,
    });
    const { getMessaging, isSupported } = await import("firebase/messaging");
    if (!(await isSupported())) return null;
    messagingSingleton = { messaging: getMessaging(app), vapid: cfg.web.vapid_public_key, registration };
    return messagingSingleton;
  })();

  const result = await inFlight;
  inFlight = null;
  return result;
}

export async function requestPushPermission(): Promise<NotificationPermission> {
  if (!isPushSupported()) return "denied";
  const current = Notification.permission;
  if (current === "granted" || current === "denied") return current;
  return Notification.requestPermission();
}

export async function getPushToken(): Promise<string | null> {
  const bundle = await initMessaging();
  if (!bundle) return null;
  const { getToken } = await import("firebase/messaging");
  try {
    const token = await getToken(bundle.messaging, {
      vapidKey: bundle.vapid,
      serviceWorkerRegistration: bundle.registration,
    });
    return token || null;
  } catch {
    return null;
  }
}

export async function onForegroundMessage(cb: (payload: any) => void): Promise<() => void> {
  const bundle = await initMessaging();
  if (!bundle) return () => {};
  const { onMessage } = await import("firebase/messaging");
  return onMessage(bundle.messaging, cb);
}

export async function registerPushToken(token: string, extra?: { device_label?: string }) {
  try {
    return (await api.post("/push/register", {
      token,
      platform: "web",
      device_label: extra?.device_label,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 400) : undefined,
    })).data;
  } catch (e) {
    return null;
  }
}

export async function unregisterPushToken(token: string) {
  try {
    return (await api.post("/push/unregister", { token })).data;
  } catch {
    return null;
  }
}
